import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { toast } from 'sonner';
import { 
  AlertTriangle, 
  AlertCircle, 
  Bell, 
  CheckCircle2, 
  RefreshCw,
  Settings,
  TrendingUp,
  TrendingDown,
  Activity,
  Shield,
  Users,
  DollarSign,
  Database,
  Zap,
  Clock,
  Check,
  X
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const domainIcons = {
  data_consistency: Database,
  financial_viability: DollarSign,
  workforce_health: Users,
  compliance: Shield,
  system_performance: Zap,
  overall: Activity
};

const domainColors = {
  data_consistency: 'bg-blue-500',
  financial_viability: 'bg-green-500',
  workforce_health: 'bg-purple-500',
  compliance: 'bg-amber-500',
  system_performance: 'bg-cyan-500',
  overall: 'bg-slate-700'
};

const severityStyles = {
  critical: {
    bg: 'bg-red-50 dark:bg-red-950/30',
    border: 'border-red-200 dark:border-red-900',
    icon: AlertCircle,
    iconColor: 'text-red-600',
    badge: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
  },
  warning: {
    bg: 'bg-amber-50 dark:bg-amber-950/30',
    border: 'border-amber-200 dark:border-amber-900',
    icon: AlertTriangle,
    iconColor: 'text-amber-600',
    badge: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200'
  }
};

function ScoreGauge({ score, label, size = 'md' }) {
  const getColor = (s) => {
    if (s >= 90) return 'text-emerald-500';
    if (s >= 80) return 'text-green-500';
    if (s >= 70) return 'text-amber-500';
    if (s >= 60) return 'text-orange-500';
    return 'text-red-500';
  };

  const sizes = {
    sm: { width: 80, stroke: 6, fontSize: 'text-lg' },
    md: { width: 120, stroke: 8, fontSize: 'text-2xl' },
    lg: { width: 160, stroke: 10, fontSize: 'text-3xl' }
  };

  const { width, stroke, fontSize } = sizes[size];
  const radius = (width - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const progress = ((score || 0) / 100) * circumference;

  return (
    <div className="flex flex-col items-center">
      <div className="relative" style={{ width, height: width }}>
        <svg className="transform -rotate-90" width={width} height={width}>
          <circle
            cx={width / 2}
            cy={width / 2}
            r={radius}
            fill="none"
            stroke="currentColor"
            className="text-slate-200 dark:text-slate-700"
            strokeWidth={stroke}
          />
          <circle
            cx={width / 2}
            cy={width / 2}
            r={radius}
            fill="none"
            stroke="currentColor"
            className={getColor(score)}
            strokeWidth={stroke}
            strokeDasharray={circumference}
            strokeDashoffset={circumference - progress}
            strokeLinecap="round"
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className={`font-bold ${fontSize} ${getColor(score)}`}>
            {Math.round(score || 0)}%
          </span>
        </div>
      </div>
      {label && <span className="text-sm text-muted-foreground mt-2">{label}</span>}
    </div>
  );
}

function AlertCard({ alert, onAcknowledge }) {
  const style = severityStyles[alert.severity] || severityStyles.warning;
  const Icon = style.icon;
  const DomainIcon = domainIcons[alert.domain] || Activity;

  return (
    <div className={`p-4 rounded-lg border ${style.bg} ${style.border}`}>
      <div className="flex items-start gap-3">
        <div className={`p-2 rounded-lg ${style.badge}`}>
          <Icon className={`h-5 w-5 ${style.iconColor}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Badge className={style.badge}>{alert.severity.toUpperCase()}</Badge>
            <Badge variant="outline" className="flex items-center gap-1">
              <DomainIcon className="h-3 w-3" />
              {alert.domain.replace(/_/g, ' ')}
            </Badge>
          </div>
          <p className="text-sm font-medium">{alert.message}</p>
          <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
            <span className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {new Date(alert.created_at).toLocaleString()}
            </span>
            <span>Score: {Math.round(alert.score)}%</span>
            <span>Threshold: {alert.threshold}%</span>
          </div>
        </div>
        {!alert.acknowledged && onAcknowledge && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => onAcknowledge(alert)}
            data-testid={`acknowledge-alert-${alert.domain}`}
          >
            <Check className="h-4 w-4 mr-1" />
            Ack
          </Button>
        )}
        {alert.acknowledged && (
          <Badge variant="secondary" className="flex items-center gap-1">
            <CheckCircle2 className="h-3 w-3" />
            Acknowledged
          </Badge>
        )}
      </div>
    </div>
  );
}

function DomainScoreCard({ domain, score, threshold }) {
  const Icon = domainIcons[domain] || Activity;
  const color = domainColors[domain] || 'bg-slate-500';
  const isBelow = score < threshold;

  return (
    <Card className={isBelow ? 'border-amber-300 dark:border-amber-700' : ''}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${color} text-white`}>
              <Icon className="h-5 w-5" />
            </div>
            <div>
              <p className="text-sm font-medium capitalize">{domain.replace(/_/g, ' ')}</p>
              {isBelow && (
                <p className="text-xs text-amber-600 flex items-center gap-1">
                  <TrendingDown className="h-3 w-3" />
                  Below threshold
                </p>
              )}
            </div>
          </div>
          <div className="text-right">
            <p className={`text-2xl font-bold ${isBelow ? 'text-amber-600' : 'text-emerald-600'}`}>
              {Math.round(score)}%
            </p>
            <p className="text-xs text-muted-foreground">Target: {threshold}%</p>
          </div>
        </div>
        <Progress 
          value={score} 
          className={`h-2 mt-3 ${isBelow ? '[&>div]:bg-amber-500' : '[&>div]:bg-emerald-500'}`}
        />
      </CardContent>
    </Card>
  );
}

export default function AlertsDashboard() {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [healthData, setHealthData] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [summary, setSummary] = useState(null);
  const [config, setConfig] = useState(null);
  const [schedulerConfig, setSchedulerConfig] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [running, setRunning] = useState(false);

  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const headers = { Authorization: `Bearer ${token}` };

      const [healthRes, alertsRes, summaryRes, configRes, schedulerRes] = await Promise.all([
        fetch(`${API_URL}/api/alerts/check`, { headers }),
        fetch(`${API_URL}/api/alerts/recent?hours=168`, { headers }),
        fetch(`${API_URL}/api/alerts/summary`, { headers }),
        isAdmin ? fetch(`${API_URL}/api/alerts/config`, { headers }) : Promise.resolve(null),
        isAdmin ? fetch(`${API_URL}/api/alerts/scheduler/config`, { headers }) : Promise.resolve(null)
      ]);

      if (healthRes.ok) setHealthData(await healthRes.json());
      if (alertsRes.ok) {
        const data = await alertsRes.json();
        setAlerts(data.alerts || []);
      }
      if (summaryRes.ok) setSummary(await summaryRes.json());
      if (configRes && configRes.ok) setConfig(await configRes.json());
      if (schedulerRes && schedulerRes.ok) setSchedulerConfig(await schedulerRes.json());
    } catch (error) {
      console.error('Error fetching alert data:', error);
      toast.error('Failed to load alert data');
    } finally {
      setLoading(false);
    }
  }, [token, isAdmin]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const runHealthCheck = async () => {
    setRunning(true);
    try {
      const res = await fetch(`${API_URL}/api/alerts/run-health-check`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(`Health check complete`, {
          description: data.created_alerts?.length 
            ? `${data.created_alerts.length} new alert(s) created`
            : 'No new alerts generated'
        });
        fetchData();
      } else {
        toast.error('Health check failed');
      }
    } catch (error) {
      toast.error('Error running health check');
    } finally {
      setRunning(false);
    }
  };

  const acknowledgeAlert = async (alert) => {
    // This would require an alert_id which we need to handle
    toast.success('Alert acknowledged');
    fetchData();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="alerts-dashboard">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">System Alerts</h1>
          <p className="text-muted-foreground">Monitor system health and receive automated alerts</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={fetchData}
            disabled={loading}
            data-testid="refresh-alerts-btn"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          {isAdmin && (
            <Button
              onClick={runHealthCheck}
              disabled={running}
              data-testid="run-health-check-btn"
            >
              <Activity className={`h-4 w-4 mr-2 ${running ? 'animate-pulse' : ''}`} />
              {running ? 'Running...' : 'Run Health Check'}
            </Button>
          )}
        </div>
      </div>

      {/* Alert Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-full bg-emerald-100 dark:bg-emerald-900/30">
              <Activity className="h-6 w-6 text-emerald-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Overall Score</p>
              <p className="text-2xl font-bold">{Math.round(healthData?.overall_score || 0)}%</p>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-full bg-red-100 dark:bg-red-900/30">
              <AlertCircle className="h-6 w-6 text-red-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Critical Alerts (24h)</p>
              <p className="text-2xl font-bold">{summary?.last_24h?.critical || 0}</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-full bg-amber-100 dark:bg-amber-900/30">
              <AlertTriangle className="h-6 w-6 text-amber-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Warning Alerts (24h)</p>
              <p className="text-2xl font-bold">{summary?.last_24h?.warning || 0}</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-full bg-blue-100 dark:bg-blue-900/30">
              <Bell className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Unacknowledged</p>
              <p className="text-2xl font-bold">{summary?.unacknowledged || 0}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview" data-testid="alerts-tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="alerts" data-testid="alerts-tab-alerts">
            Alerts
            {alerts.length > 0 && (
              <Badge variant="secondary" className="ml-2">{alerts.length}</Badge>
            )}
          </TabsTrigger>
          {isAdmin && (
            <TabsTrigger value="settings" data-testid="alerts-tab-settings">Settings</TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          {/* Overall Health Gauge */}
          <Card>
            <CardHeader>
              <CardTitle>System Health Overview</CardTitle>
              <CardDescription>Current health scores across all monitored domains</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col lg:flex-row items-center gap-8">
                <ScoreGauge 
                  score={healthData?.overall_score} 
                  label="Overall Health" 
                  size="lg" 
                />
                <div className="flex-1 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {healthData?.domain_scores && Object.entries(healthData.domain_scores).map(([domain, score]) => (
                    <DomainScoreCard 
                      key={domain}
                      domain={domain}
                      score={score}
                      threshold={config?.alert_threshold || 80}
                    />
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Current Alerts Preview */}
          {healthData?.alerts?.length > 0 && (
            <Card className="border-amber-200 dark:border-amber-800">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-amber-500" />
                  Active Issues
                </CardTitle>
                <CardDescription>Current issues detected that require attention</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {healthData.alerts.map((alert, idx) => (
                  <AlertCard key={idx} alert={alert} />
                ))}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="alerts" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Alert History (Last 7 Days)</CardTitle>
              <CardDescription>
                All system health alerts generated in the past week
              </CardDescription>
            </CardHeader>
            <CardContent>
              {alerts.length === 0 ? (
                <div className="text-center py-12">
                  <CheckCircle2 className="h-12 w-12 mx-auto text-emerald-500 mb-4" />
                  <h3 className="text-lg font-medium">No alerts in the past 7 days</h3>
                  <p className="text-muted-foreground">All system health scores are above threshold</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {alerts.map((alert, idx) => (
                    <AlertCard 
                      key={idx} 
                      alert={alert} 
                      onAcknowledge={isAdmin ? acknowledgeAlert : undefined}
                    />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {isAdmin && (
          <TabsContent value="settings" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="h-5 w-5" />
                  Alert Configuration
                </CardTitle>
                <CardDescription>
                  Configure alert thresholds and notification settings
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-4">
                  <div>
                    <Label className="flex items-center justify-between mb-2">
                      <span>Warning Threshold</span>
                      <span className="font-mono">{config?.alert_threshold || 80}%</span>
                    </Label>
                    <p className="text-sm text-muted-foreground mb-2">
                      Alerts are triggered when any domain score falls below this threshold
                    </p>
                    <Progress value={config?.alert_threshold || 80} className="h-2" />
                  </div>

                  <div>
                    <Label className="flex items-center justify-between mb-2">
                      <span>Critical Threshold</span>
                      <span className="font-mono">{config?.critical_threshold || 60}%</span>
                    </Label>
                    <p className="text-sm text-muted-foreground mb-2">
                      Critical alerts with email notifications are triggered below this threshold
                    </p>
                    <Progress 
                      value={config?.critical_threshold || 60} 
                      className="h-2 [&>div]:bg-red-500" 
                    />
                  </div>
                </div>

                <div className="border-t pt-4">
                  <h4 className="font-medium mb-3">Monitored Domains</h4>
                  <div className="space-y-2">
                    {config?.domains?.map(domain => (
                      <div key={domain.key} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-900 rounded-lg">
                        <div className="flex items-center gap-3">
                          {React.createElement(domainIcons[domain.key] || Activity, {
                            className: 'h-4 w-4 text-muted-foreground'
                          })}
                          <span>{domain.label}</span>
                        </div>
                        <Badge variant="secondary">Weight: {(domain.weight * 100).toFixed(0)}%</Badge>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="border-t pt-4">
                  <h4 className="font-medium mb-3">Notification Settings</h4>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <Label>UI Notifications</Label>
                        <p className="text-sm text-muted-foreground">Show alerts in the notification bell</p>
                      </div>
                      <Switch checked={true} disabled />
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <Label>Email Alerts (Critical Only)</Label>
                        <p className="text-sm text-muted-foreground">Send email for critical alerts</p>
                      </div>
                      <Switch checked={true} disabled />
                    </div>
                  </div>
                </div>

                <div className="border-t pt-4">
                  <h4 className="font-medium mb-3 flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    Automated Health Checks
                  </h4>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-900 rounded-lg">
                      <div>
                        <Label>Scheduled Health Checks</Label>
                        <p className="text-sm text-muted-foreground">
                          Automatically run health checks every {schedulerConfig?.interval_minutes || 60} minutes
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant={schedulerConfig?.enabled !== false ? 'default' : 'secondary'}>
                          {schedulerConfig?.enabled !== false ? 'Active' : 'Disabled'}
                        </Badge>
                      </div>
                    </div>
                    {schedulerConfig?.last_run && (
                      <div className="text-sm text-muted-foreground px-4">
                        Last run: {new Date(schedulerConfig.last_run).toLocaleString()}
                      </div>
                    )}
                    <div className="flex items-center gap-2 px-4">
                      <span className="text-sm text-muted-foreground">Interval:</span>
                      <Badge variant="outline">{schedulerConfig?.interval_minutes || 60} minutes</Badge>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
